// Build: freeze the design as a new version and (re)create proj_<slug> on the project
// PostgreSQL (POST .../dbdesign/build). The server answers 202 at once and builds in the
// background; this panel polls GET .../versions/{n} until the version is built or failed,
// showing the build log as it grows — also when the page is opened while a build runs.
// Shows the latest status, the psql connection hint, the downloads (.sql dump,
// datapackage.json, the data package zip with one CSV per table, the version's LinkML schema, its
// spreadsheet template (one sheet per table), and for a built version the RO-Crate zip) and the version history
// (each version also offers each table's own template behind "Templates per table"). A HOSTED database (ws.hosting: open for data
// entry) is never rebuilt — the server answers 409 — so Build is disabled and the panel says why;
// it changes through a migration instead (MigrationPanel, below the hosted note); its staged
// columns (fill, then tighten) are listed above it (ColumnStagesPanel).
// HostingPanel (Host / Stop hosting, the explaining dialog) sits under the header, and
// DeletedSourcesPanel (a deleted source: stale build, drop, dump purge, hosted row removal) under it.
import { useEffect, useRef, useState } from "react";
import { api } from "../../api";
import { Badge, Btn, ConfirmDialog, Spinner } from "../../components/Shared";
import { ErrorNote } from "../../components/Feedback";
import DeletedSourcesPanel from "./DeletedSourcesPanel";
import HostingPanel from "./HostingPanel";
import MigrationPanel from "./MigrationPanel";
import RestructurePanel from "./RestructurePanel";
import ColumnStagesPanel from "./ColumnStagesPanel";
import TemplateDownloads from "../operate/TemplateDownloads";

const STATUS = {
  built: { color: "green", glyph: "✓" }, failed: { color: "red", glyph: "✕" },
  building: { color: "amber", glyph: "◐" }, frozen: { color: "gray", glyph: "▪" },
  migrating: { color: "amber", glyph: "◐" }, migrated: { color: "green", glyph: "✓" },
};

export function VersionBadge({ status }) {
  const s = STATUS[status] || { color: "gray", glyph: "" };
  return <Badge color={s.color}><span aria-hidden="true" style={{ marginRight: 4 }}>{s.glyph}</span>{status}</Badge>;
}

const code = {
  display: "block", fontFamily: "'JetBrains Mono', monospace", fontSize: 13, background: "#f3f4f6",
  border: "1px solid #e5e7eb", borderRadius: 6, padding: "6px 10px", overflowX: "auto", whiteSpace: "pre",
};

function Downloads({ projectId, v }) {
  const [error, setError] = useState(null);
  const base = `/projects/${projectId}/dbdesign/versions/${v.version}`;
  const get = (path, name) => api.download(`${base}/${path}`, name).catch(setError);
  return (
    <span style={{ display: "inline-flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
      {v.has_sql && (
        <Btn variant="secondary" style={{ padding: "5px 12px", fontSize: 13 }}
             onClick={() => get("schema.sql", `${v.database_name}_v${v.version}.sql`)}>
          .sql<span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}> of version {v.version}</span>
        </Btn>
      )}
      {v.has_datapackage && (
        <Btn variant="secondary" style={{ padding: "5px 12px", fontSize: 13 }}
             onClick={() => get("datapackage.json", `${v.database_name}_v${v.version}.datapackage.json`)}>
          datapackage.json<span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}> of version {v.version}</span>
        </Btn>
      )}
      {/* Every version (also a failed one) has its LinkML schema, written from the frozen design. */}
      <Btn variant="secondary" style={{ padding: "5px 12px", fontSize: 13 }}
           onClick={() => get("schema.linkml.yaml", `${v.database_name}_v${v.version}.linkml.yaml`)}>
        LinkML<span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}> of version {v.version}</span>
      </Btn>
      {/* A spreadsheet template of every table, from the frozen dictionary (routers/sheet_templates.py). */}
      <Btn variant="secondary" style={{ padding: "5px 12px", fontSize: 13 }}
           title="An XLSX to fill in: one sheet per table, with the dictionary's drop-downs and checks in its cells"
           onClick={() => get("template.xlsx", `${v.database_name}_v${v.version}.template.xlsx`)}>
        Template (XLSX)<span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}> of version {v.version}</span>
      </Btn>
      {v.has_datapackage_zip && (
        <Btn variant="secondary" style={{ padding: "5px 12px", fontSize: 13 }}
             title="datapackage.json with one CSV per table, exported from the built database"
             onClick={() => get("datapackage.zip", `${v.database_name}_v${v.version}.datapackage.zip`)}>
          data package (.zip)<span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}> of version {v.version}</span>
        </Btn>
      )}
      {v.has_migration_sql && (
        <Btn variant="secondary" style={{ padding: "5px 12px", fontSize: 13 }}
             onClick={() => get("migration.sql", `${v.database_name}_v${v.version}_migration.sql`)}>
          migration.sql<span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}> of version {v.version}</span>
        </Btn>
      )}
      {v.status === "built" && v.has_sql && v.has_datapackage && (
        <Btn variant="secondary" style={{ padding: "5px 12px", fontSize: 13 }}
             title="The .sql script, datapackage.json, the frozen design and its provenance as one RO-Crate zip"
             onClick={() => get("ro-crate", `${v.database_name}-v${v.version}.zip`)}>
          Download RO-Crate<span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}> of version {v.version}</span>
        </Btn>
      )}
      {error && <span role="alert" style={{ color: "#b91c1c", fontSize: 12 }}>{error.message}</span>}
    </span>
  );
}

// Each table of a version downloads its own template (XLSX, header CSV, README), so a member who
// fills in one table need not take every table's sheet. Collapsed: a version may have 20 tables.
// v.template_tables is the list the per-table route accepts (composite hubs left out).
function TableTemplates({ projectId, v }) {
  const [open, setOpen] = useState(false);
  const tables = v.template_tables || [];
  if (tables.length === 0) return null;
  return (
    <>
      <button type="button" aria-expanded={open} onClick={() => setOpen(o => !o)}
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--dp-accent-dark)", fontSize: 13, fontFamily: "inherit" }}>
        {open ? "Hide" : "Templates per table"}
        <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>{open ? " templates per table" : ""} of version {v.version}</span>
      </button>
      {open && (
        <ul aria-label={`Tables of version ${v.version}`} style={{ flexBasis: "100%", listStyle: "none", margin: "4px 0 0", padding: "0 0 0 16px" }}>
          {tables.map(t => (
            <li key={t} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", padding: "4px 0" }}>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", minWidth: 160, overflowWrap: "anywhere" }}>{t}</span>
              <TemplateDownloads projectId={projectId} base={`/projects/${projectId}/dbdesign/versions/${v.version}`}
                                 table={t} stem={`${v.database_name}_v${v.version}_${t}`}
                                 label={` of ${t}, version ${v.version}`} />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// How often a running build is polled. Tests shorten it.
export const buildPolling = { intervalMs: 2000 };

export default function BuildPanel({ ws, versions, canManage, onChange }) {
  const [starting, setStarting] = useState(false);
  const [watching, setWatching] = useState(null);   // the version number this page started
  const [polled, setPolled] = useState(null);       // the followed version as last polled
  const [finished, setFinished] = useState(null);   // last version seen leaving "building"
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);
  const [showLog, setShowLog] = useState(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const saved = ws.latest_version;
  const problems = ws.design.problems || [];
  const hosting = ws.hosting || null;
  const blocked = problems.length > 0 || !ws.database_name || !!hosting;
  const projectId = ws.project_id;

  // Follow a build this page started, or one already running when the page was opened.
  const target = watching ?? (saved && saved.status === "building" && saved.version !== finished ? saved.version : null);
  useEffect(() => {
    if (target == null) return undefined;
    let live = true;
    let timer;
    const tick = async () => {
      try {
        const v = await api.get(`/projects/${projectId}/dbdesign/versions/${target}`);
        if (!live) return;
        setError(null);
        setPolled(v);
        if (v.status === "building") {
          timer = setTimeout(tick, buildPolling.intervalMs);
        } else {
          setFinished(target);
          setWatching(null);
          setShowLog(true);
          onChangeRef.current();
        }
      } catch (e) {
        if (!live) return;
        setError(e);                                     // keep following; it may be a blip
        timer = setTimeout(tick, buildPolling.intervalMs);
      }
    };
    timer = setTimeout(tick, buildPolling.intervalMs);
    return () => { live = false; clearTimeout(timer); };
  }, [target, projectId]);

  const build = async () => {
    setConfirming(false);
    setStarting(true);
    setError(null);
    try {
      const v = await api.post(`/projects/${projectId}/dbdesign/build`);
      setPolled(v);
      setWatching(v.version);
      setShowLog(true);
    } catch (e) {
      setError(e);                                       // 409: another build is running
    } finally {
      setStarting(false);
      onChange();                                        // the new version appears as "building"
    }
  };
  const running = starting || target != null;
  // Show the followed version as the poll last saw it until the workspace has caught up.
  const pollIsNewer = polled && (!saved || polled.version > saved.version
                                 || (polled.version === saved.version && saved.status === "building"));
  const latest = pollIsNewer ? polled : saved;
  const start = () => (latest && latest.status === "built" ? setConfirming(true) : build());

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>Project database</div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 18, fontWeight: 700, marginTop: 2 }}>
            {ws.database_name || "–"}
          </div>
          <div role="status" style={{ marginTop: 6, fontSize: 13, color: "#374151", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {running ? <><Spinner /> {target != null ? `Building version ${target}…` : "Starting the build…"}</> : latest ? (
              <>
                <VersionBadge status={latest.status} />
                <span>version {latest.version}{latest.built_at ? `, built ${new Date(latest.built_at).toLocaleString()}` : ""}</span>
              </>
            ) : <span>Not built yet.</span>}
          </div>
        </div>
        {canManage && (
          <Btn onClick={start} disabled={running || blocked}>
            {latest ? "Rebuild database" : "Build database"}
          </Btn>
        )}
      </div>

      <ErrorNote error={error} />
      {!running && (
        <HostingPanel projectId={projectId} onChange={onChange}
                      refreshKey={`${latest ? `${latest.version}:${latest.status}` : "-"}/${hosting ? hosting.version : "-"}`} />
      )}
      {hosting && (
        <div data-testid="hosted-note" style={{ marginTop: 14, padding: "10px 14px", borderRadius: 8, background: "rgba(37,99,235,0.06)", border: "1px solid rgba(37,99,235,0.25)", color: "#1e3a8a", fontSize: 13 }}>
          <strong>{hosting.database_name || ws.database_name} is hosted</strong> (version {hosting.version}
          {hosting.hosted_at ? `, since ${new Date(hosting.hosted_at).toLocaleString()}` : ""}
          {hosting.hosted_by ? ` by ${hosting.hosted_by}` : ""}): it is open for data entry, so it is
          not rebuilt — a build would drop it and every row entered into it. Changes to a hosted
          database go through a migration (below). To rebuild it from scratch, an admin first ends hosting.
        </div>
      )}
      {!running && (
        <DeletedSourcesPanel projectId={projectId} canManage={canManage} onChange={onChange}
                             refreshKey={`${latest ? `${latest.version}:${latest.status}` : "-"}/${hosting ? hosting.version : "-"}`} />
      )}
      {hosting && <ColumnStagesPanel projectId={projectId} canManage={canManage}
                                     refreshKey={`${hosting.version}/${latest ? latest.status : "-"}`} />}
      {hosting && <MigrationPanel ws={ws} versions={versions} canManage={canManage} onChange={onChange} />}
      {hosting && canManage && <RestructurePanel ws={ws} onChange={onChange} />}
      {problems.length > 0 && (
        <div role="alert" style={{ marginTop: 14, padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#991b1b", fontSize: 13 }}>
          <strong>The design cannot be built yet:</strong>
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>{problems.map((p, i) => <li key={i}>{p}</li>)}</ul>
        </div>
      )}
      {(ws.design.warnings || []).length > 0 && (
        <ul style={{ margin: "12px 0 0", paddingLeft: 18, fontSize: 13, color: "#92400e" }}>
          {ws.design.warnings.map((w, i) => <li key={i}>{w}</li>)}
        </ul>
      )}

      {!running && latest && latest.status === "built" && ws.connection && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, color: "#374151", marginBottom: 6 }}>Connect with psql:</div>
          <code style={code}>{ws.connection.compose_psql}</code>
          <code style={{ ...code, marginTop: 6 }}>{ws.connection.psql}</code>
        </div>
      )}
      {latest && latest.build_log && (
        <div style={{ marginTop: 12 }}>
          <button type="button" aria-expanded={showLog} onClick={() => setShowLog(s => !s)}
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--dp-accent-dark)", fontSize: 13, fontFamily: "inherit" }}>
            {showLog ? "Hide" : "Show"} build log
          </button>
          {showLog && <pre data-testid="build-log" style={{ ...code, marginTop: 6, maxHeight: 260 }}>{latest.build_log}</pre>}
        </div>
      )}

      {versions.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <h3 style={{ fontSize: 14, margin: "0 0 8px" }}>Versions</h3>
          <ul aria-label="Design versions" style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {versions.map(v => (
              <li key={v.version} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", padding: "6px 0", borderTop: "1px solid #e5e7eb", fontSize: 13 }}>
                <strong>v{v.version}</strong>
                <VersionBadge status={v.status} />
                <span style={{ color: "#6b7280" }}>
                  {v.table_count} tables · {v.foreign_key_count} foreign keys
                  {v.created_at ? ` · ${new Date(v.created_at).toLocaleString()}` : ""}
                </span>
                <Downloads projectId={ws.project_id} v={v} />
                <TableTemplates projectId={ws.project_id} v={v} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {confirming && (
        <ConfirmDialog title={`Rebuild ${ws.database_name}?`} confirmWord={ws.database_name}
                       confirmLabel="Rebuild" onConfirm={build} onClose={() => setConfirming(false)}>
          <p style={{ margin: 0, fontSize: 14, color: "#374151" }}>
            The database is dropped and created again from the current design. Anything written into
            it since the last build is lost. The previous version's .sql stays downloadable.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}
